import {ModalToolContainerService} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service';
import {ProjectId, SpecGroupId, SpecId} from '@webmate-web-core/shared/types/ids';
import {Component, ElementRef, HostBinding, Input, Output, ViewChild} from '@angular/core';
import {
    faArrowDown,
    faArrowLeft,
    faArrowRight,
    faArrowUp,
    faChevronDown,
    faChevronLeft,
    faChevronRight,
    faEdit,
    faEye,
    faFilePdf,
    faGlobe,
    faPlus,
    faQuestionCircle,
    faSave,
    faTimes,
    faTrash
} from '@fortawesome/pro-solid-svg-icons';
import {map, switchMap} from 'rxjs/operators';
import {FormBuilder, FormsModule, ReactiveFormsModule, UntypedFormGroup} from '@angular/forms';
import {FormsCommonsModule} from '@webmate-web-core/shared/components/forms-commons/forms-commons.module';
import {CommonModule} from '@angular/common';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {TabViewModule} from 'primeng/tabview';
import {ButtonModule} from 'primeng/button';
import {TooltipModule} from 'primeng/tooltip';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {InputTextModule} from 'primeng/inputtext';
import {ReportSectionContent, ReportSectionId} from '../../../../../report/report_types';
import {plusDown, plusRightDown, plusUp} from '@webmate-web-core/shared/icons';
import {Spec, SpecGroup} from '@webmate-web-core/shared/types/testmgmt';
import {
    calculateSpecForest,
    deleteNodeAndDescendandsInSpecForest,
    retrieveSpecGroupsHelper,
    SpecForestNode
} from '@webmate-web-core/shared/services/specs';
import {TestService} from '@webmate-web-core/shared/services/test.service';
import {TagsModule} from '@webmate-web-core/shared/components/tags/tags.module';
import {Tag} from '@webmate-web-core/shared/types/tag';
import {PopoverModule} from 'primeng/popover';

@Component({
    selector: 'wm-specs-tree',
    templateUrl: './specs-tree.component.html',
    styleUrls: ['./specs-tree.component.less'],
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, FormsCommonsModule, CommonModule, FontAwesomeModule, TabViewModule, ButtonModule, TooltipModule, ProgressSpinnerModule, InputTextModule, TagsModule, PopoverModule],
    animations: []
})
export class SpecsTreeComponent {

    @ViewChild('nodesContainer', {static: false}) nodesContainer: ElementRef;

    form: UntypedFormGroup;

    @Input()
    specIds: SpecId[];

    @Input()
    readonly: boolean = false;

    @Input()
    projectId: ProjectId;

    @HostBinding('class.readonly')
    get readonlyClass() {
        return this.readonly;
    }

    // emitted when specs or spec groups have been deleted
    @Output()
    nodesDeleted = new Subject<{deletedSpecs: SpecId[], deletedSpecGroups: SpecGroupId[]}>();

    // emitted when specs have been added
    @Output()
    specsAdded = new Subject<SpecId[]>();

    specs: Spec[] = [];
    specsById: { [s: string /* SpecId */]: Spec } = {};

    specGroupsById: { [s: string /* SpecGroupId */]: SpecGroup } = {};
    specGroups: SpecGroup[] = [];

    specForest: SpecForestNode[] = [];

    loadingSpecs = false;

    nodeIsExpanded: { [sectionId: string]: boolean } = {};

    editedNodeTitles: {[nodeId: string]: {content: string, original: string}} = {};

    disableNodeActions: boolean = false;
    currentNodeAction = {
        currentNodeId: '',
        visible: false,
        right: 0,
        top: 0,
        isFirst: false,
        isLast: false,
        isGroup: false
    };

    static SectionActionsOffset = 10;

    sectionNumbers: { [sectionId: string]: string } = {};

    contentNeedsUpdate$ = new BehaviorSubject<void>(undefined);
    pendingScrollSpecId: SpecId | null = null;
    pendingEditNodeTitleIds: string[] = [];

    constructor(private fb: FormBuilder,
                private testService: TestService,
                private toolService: ModalToolContainerService) {
    }

    fetchSpecs(specIds: SpecId[]): Observable<{ specs: Spec[], groups: SpecGroup[] }> {
        return this.testService.getSpecs$(specIds).pipe(
            switchMap((specs: Spec[]) => {
                return retrieveSpecGroupsHelper([], specs.map(s => s.groupId).filter((g): g is SpecGroupId => !!g), this.testService)
                    .pipe(map(groups => ({specs, groups})));
            })
        );
    }

    expandFirstLevelIfSingleGroup() {
        if (this.specForest.length === 1) {
            const firstNode = this.specForest[0];
            if (firstNode.type === 'group') {
                this.nodeIsExpanded[firstNode.id] = true;
            }
        }
    }

    ngOnChanges() {
        this.loadingSpecs = true;
        this.contentNeedsUpdate$.pipe(
            switchMap(_ => this.fetchSpecs(this.specIds))
        ).subscribe({
            next: ({specs, groups}) => {
                this.specsById = specs.reduce((acc, s) => ({...acc, [s.id]: s}), {});
                this.specGroups = groups;
                this.specGroupsById = groups.reduce((acc, g) => ({...acc, [g.id]: g}), {});
                this.specForest = calculateSpecForest(specs, groups, '');
                this.expandFirstLevelIfSingleGroup();
                this.expandPathToNode(this.specForest, this.pendingScrollSpecId);
                this.pendingEditNodeTitleIds.forEach(nodeId => this.expandPathToNode(this.specForest, nodeId));
                this.loadingSpecs = false;
                this.tryScrollToPendingSpec();
                this.tryEditPendingNodeTitles();
            },
            error: err => {
                console.error('Error loading report', err);
                this.loadingSpecs = false;
            }
        });
    }

    refreshSpec(specId: SpecId) {
        this.testService.getSpec$(specId).subscribe({
            next: spec => {
                this.specsById[specId] = spec;
            },
            error: err => {
                console.error('Error refreshing spec', err);
            }
        });
    }

    refreshSpecGroup(group: SpecGroupId) {
        this.testService.getSpecGroup$(group).subscribe({
            next: group => {
                this.specGroupsById[group.id] = group;
            },
            error: err => {
                console.error('Error refreshing spec group', err);
            }
        });
    }


    hideNodeActions() {
        this.currentNodeAction.visible = false;
        this.currentNodeAction.currentNodeId = '';
    }

    nodeHovered(node: SpecForestNode, event: MouseEvent) {
        if (event.target === null) {
            this.currentNodeAction.visible = false;
        } else {
            this.currentNodeAction.currentNodeId = node.id;
            this.currentNodeAction.isGroup = node.type === 'group';

            const target = event.target as HTMLElement;
            const containerElement = this.nodesContainer.nativeElement as HTMLElement;
            const containerRect = containerElement.getBoundingClientRect();

            this.currentNodeAction.visible = true;
            this.currentNodeAction.right = target.getBoundingClientRect().x - containerRect.x + target.getBoundingClientRect().width;// + EditReportToolComponent.SectionActionsOffset;
            this.currentNodeAction.top = target.offsetTop - (100 /* height of actions box */ / 2) + target.getBoundingClientRect().height / 2 /* center with target */;

            this.currentNodeAction.isFirst = node.id === this.specForest[0].id;
            this.currentNodeAction.isLast = node.id === this.specForest[this.specForest.length - 1].id;

        }
    }

    editNodeTitle(nodeId: string, originalTitle: string) {
        this.editedNodeTitles[nodeId] = {
            content: originalTitle,
            original: originalTitle
        };
        this.disableNodeActions = Object.keys(this.editedNodeTitles).length > 0;
    }

    cancelSaveSectionTitle(nodeId: string) {
        delete this.editedNodeTitles[nodeId];
        this.disableNodeActions = Object.keys(this.editedNodeTitles).length > 0;
    }

    saveNodeName(node: SpecForestNode) {
        const editedTitle = this.editedNodeTitles[node.id];

        if (!editedTitle) {
            return;
        }

        if (!editedTitle.content || editedTitle.content === editedTitle.original) {
            this.cancelSaveSectionTitle(node.id);
            return;
        }

        if (node.type === 'item') {
            this.testService.updateSpec$(node.id, [{
                type: 'SetName',
                name: editedTitle.content
            }]).subscribe({
                next: _ => {
                    this.cancelSaveSectionTitle(node.id);
                    this.refreshSpec(node.id);
                },
                error: err => {
                    console.error('Error updating spec title', err);
                    this.disableNodeActions = Object.keys(this.editedNodeTitles).length > 0;
                }
            });
        } else {
            this.testService.updateSpecGroup$(node.id, [{
                type: 'SetName',
                name: editedTitle.content
            }]).subscribe({
                next: _ => {
                    this.cancelSaveSectionTitle(node.id);
                    this.refreshSpecGroup(node.id);
                },
                error: err => {
                    console.error('Error updating group title', err);
                    this.disableNodeActions = Object.keys(this.editedNodeTitles).length > 0;
                }
            });
        }
    }

    addChildSpec(group: SpecGroupId, editNodeTitleIds: string[] = []) {
        // get all specs in the group
        const specsInGroup = this.specForest.find(node => node.id === group)?.children || [];
        const lastSpec = specsInGroup[specsInGroup.length - 1];
        const lastSpecDisplayId = lastSpec?.node.displayId || '';
        const nextDisplayId = lastSpecDisplayId.replace(/(\d+)$/, (_: any, num: string) => (parseInt(num) + 1).toString());

        this.testService.createSpec(this.projectId, {
            title: 'New Spec',
            description: '',
            groupId: group,
            displayId: nextDisplayId,
        }).subscribe({
            next: newSpecId => {
                this.queueNodeTitleEdits([...editNodeTitleIds, newSpecId]);
                this.specsAdded.next([newSpecId]);
            },
            error: err => {
                console.error('Error creating spec', err);
            }
        });
    }

    createSpecGroup(specGroupId?: SpecGroupId | null) {
        const specGroupData: any = {
            name: 'New Spec Group',
            description: '',
            displayId: '',
        };

        if (specGroupId) {
            specGroupData.parentGroup = specGroupId;
        }

        this.testService.createSpecGroup(this.projectId, specGroupData).subscribe({
            next: newSpecGroupId => {
                this.queueNodeTitleEdits([newSpecGroupId]);
                this.addChildSpec(newSpecGroupId, [newSpecGroupId]);
            },
            error: err => {
                console.error('Error creating spec group', err);
            }
        });
    }

    saveSpecDisplayId(specId: SpecId, newDisplayId: string) {
        this.testService.updateSpec$(specId, [{
            type: 'SetDisplayId',
            displayId: newDisplayId
        }]).subscribe({
            error: err => {
                console.error('Error updating spec display id', err);
            }
        });
    }

    saveSpecGroupDisplayId(specGroupId: SpecGroupId, newDisplayId: string) {
        this.testService.updateSpecGroup$(specGroupId, [{
            type: 'SetDisplayId',
            displayId: newDisplayId
        }]).subscribe({
            error: err => {
                console.error('Error updating spec group display id', err);
            }
        });
    }

    saveSpecLabels(specId: SpecId, newLabels: Tag[]) {
        this.testService.updateSpec$(specId, [{
            type: 'SetLabels',
            labels: newLabels
        }]).subscribe({
            next: _ => {
                this.refreshSpec(specId);
            },
            error: err => {
                console.error('Error updating spec labels', err);
            }
        });
    }

    addChildSpecGroup(parentGroup: SpecGroupId) {
        // get all groups in the group
        const groupsInGroup = this.specForest.find(node => node.id === parentGroup)?.children || [];
        const lastGroup = groupsInGroup[groupsInGroup.length - 1];
        const lastGroupDisplayId = lastGroup?.node.displayId || '';
        const nextDisplayId = lastGroupDisplayId.replace(/(\d+)$/, (_: any, num: string) => (parseInt(num) + 1).toString());

        this.testService.createSpecGroup(this.projectId, {
            name: 'New Group',
            description: '',
            parentGroup: parentGroup,
            displayId: nextDisplayId,
        }).subscribe({
            error: err => {
                console.error('Error creating group', err);
            }
        });
    }

    deleteSpecGroup(group: SpecGroupId) {
        // delete node from forest
        const deletedNodes = deleteNodeAndDescendandsInSpecForest(this.specForest, group);
        const deletedSpecs = deletedNodes.filter(n => n.type === 'item').map(n => n.id as SpecId);
        const deletedSpecGroups = deletedNodes.filter(n => n.type === 'group').map(n => n.id as SpecGroupId);

        this.nodesDeleted.next({deletedSpecs, deletedSpecGroups});
    }

    deleteSpec(specId: SpecId) {
        const deletedNodes = deleteNodeAndDescendandsInSpecForest(this.specForest, specId);
        const deletedSpecs = deletedNodes.filter(n => n.type === 'item').map(n => n.id as SpecId);
        this.nodesDeleted.next({deletedSpecs, deletedSpecGroups: []});
    }


    updateGroupDescription(group: SpecGroupId, newDescription: string) {
        this.testService.updateSpecGroup$(group, [{
            type: "SetDescription",
            description: newDescription
        }]).subscribe({
            error: err => {
                console.error('Error updating group description', err);
            }
        });
    }

    updateSpecDescription(spec: SpecId, newDescription: string) {
        this.testService.updateSpec$(spec, [{
            type: "SetDescription",
            description: newDescription
        }]).subscribe({
            error: err => {
                console.error('Error updating spec description', err);
            }
        });
    }

    toggleNode(nodeId: string) {
        this.nodeIsExpanded[nodeId] = !this.nodeIsExpanded[nodeId];
    }

    private expandPathToNode(nodes: SpecForestNode[], targetId: string | null): boolean {
        if (!targetId) {
            return false;
        }

        for (const node of nodes) {
            if (node.id === targetId) {
                this.nodeIsExpanded[node.id] = true;
                return true;
            }

            if (this.expandPathToNode(node.children, targetId)) {
                this.nodeIsExpanded[node.id] = true;
                return true;
            }
        }

        return false;
    }

    private tryScrollToPendingSpec() {
        if (!this.pendingScrollSpecId) {
            return;
        }

        setTimeout(() => {
            const container = this.nodesContainer?.nativeElement as HTMLElement | undefined;
            const specElement = container?.querySelector(`[data-spec-id="${this.pendingScrollSpecId}"]`) as HTMLElement | null;

            if (!specElement) {
                return;
            }

            specElement.scrollIntoView({behavior: 'smooth', block: 'center'});
            this.pendingScrollSpecId = null;
        });
    }

    private queueNodeTitleEdits(nodeIds: string[]) {
        this.pendingEditNodeTitleIds = [...new Set([...this.pendingEditNodeTitleIds, ...nodeIds.filter(Boolean)])];
    }

    private tryEditPendingNodeTitles() {
        if (this.pendingEditNodeTitleIds.length === 0 || this.readonly) {
            return;
        }

        const editableNodeIds: string[] = [];
        const remainingNodeIds: string[] = [];

        for (const nodeId of this.pendingEditNodeTitleIds) {
            const spec = this.specsById[nodeId];
            if (spec) {
                this.editNodeTitle(spec.id, spec.title);
                editableNodeIds.push(spec.id);
                continue;
            }

            const group = this.specGroupsById[nodeId];
            if (group) {
                this.editNodeTitle(group.id, group.name);
                editableNodeIds.push(group.id);
                continue;
            }

            remainingNodeIds.push(nodeId);
        }

        this.pendingEditNodeTitleIds = remainingNodeIds;

        if (editableNodeIds.length === 0) {
            return;
        }

        setTimeout(() => {
            const container = this.nodesContainer?.nativeElement as HTMLElement | undefined;
            const focusNodeId = editableNodeIds[editableNodeIds.length - 1];
            const titleInput = container?.querySelector(`[data-title-edit-input="${focusNodeId}"]`) as HTMLInputElement | null;

            if (!titleInput) {
                return;
            }

            titleInput.focus();
            titleInput.select();
        });
    }

    updateContent(sectionId: ReportSectionId, contentIdx: number, newContent: ReportSectionContent) {

        // replace the content in the content array of section
        // If there is no content, we create a new array.
        // const sectionContent = this.report!.sectionContent[sectionId] || [];
        //
        // const newContentArray = sectionContent.slice(0, contentIdx);
        // newContentArray[contentIdx] = newContent;
        // newContentArray.push(...sectionContent.slice(contentIdx + 1));
        //
        // this.reportService.updateReport(this.reportId, {
        //     type: 'SetSectionContent',
        //     sectionId: sectionId,
        //     content: newContentArray
        // }).subscribe({
        //     next: _ => {
        //         this.contentNeedsUpdate$.next();
        //     },
        //     error: err => {
        //         console.error('Error updating content', err);
        //     }
        // });
    }

    protected readonly faPlus = faPlus;
    protected readonly faEye = faEye;
    protected readonly faGlobe = faGlobe;
    protected readonly faFilePdf = faFilePdf;
    protected readonly faSave = faSave;
    protected readonly faChevronDown = faChevronDown;
    protected readonly faChevronLeft = faChevronLeft;
    protected readonly faQuestionCircle = faQuestionCircle;
    protected readonly faChevronRight = faChevronRight;
    protected readonly faTrash = faTrash;
    protected readonly faEdit = faEdit;
    protected readonly faArrowLeft = faArrowLeft;
    protected readonly faArrowRight = faArrowRight;
    protected readonly faArrowUp = faArrowUp;
    protected readonly faArrowDown = faArrowDown;
    protected readonly faTimes = faTimes;
    protected readonly plusDown = plusDown;
    protected readonly plusUp = plusUp;
    protected readonly plusRightDown = plusRightDown;
}
